import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingSkeleton,
  MetricCard,
  MetricCardGrid,
  formatDate,
  formatNumber,
} from '@berky/ui'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, ChartColumn } from 'lucide-react'
import { useState } from 'react'

import {
  usePlatformUsageApi,
  type UsageModuleRank,
  type UsagePageRank,
  type UsagePageResult,
  type UsageSettings,
  type UsageSummary,
} from './api'
import {
  comparison,
  completeDays,
  duration,
  moduleTitle,
  pageTitle,
  queryFilters,
  type UsageFilters,
} from './usage-utils'

export function UsageOverview({
  filters,
  settings,
  onFilter,
}: {
  filters: UsageFilters
  settings: UsageSettings
  onFilter: (filters: UsageFilters) => void
}) {
  const api = usePlatformUsageApi()
  const [rankPage, setRankPage] = useState(1)
  const summary = useQuery({
    queryKey: ['platform-usage', 'summary', filters],
    queryFn: ({ signal }) =>
      api.get<UsageSummary>('/summary', { query: queryFilters(filters), signal }),
  })
  const ranking = useQuery({
    queryKey: ['platform-usage', 'ranking', filters, rankPage],
    queryFn: ({ signal }) =>
      api.get<UsagePageResult<UsageModuleRank>>('/ranking', {
        query: { ...queryFilters(filters), seite: rankPage },
        signal,
      }),
  })
  const report = summary.data
  const days = report ? completeDays(report.from, report.to, report.days) : []
  const maximum = Math.max(1, ...days.map((day) => day.users))
  const maxUsers = Math.max(
    1,
    ...(ranking.data?.items.map((module) => module.users) ?? []),
  )

  return (
    <div className="space-y-6">
      {summary.isPending ? (
        <LoadingSkeleton variant="text" rows={6} />
      ) : summary.isError ? (
        <ErrorState
          description="Die Kennzahlen konnten nicht geladen werden."
          onRetry={() => void summary.refetch()}
        />
      ) : (
        report && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
              <p>
                {formatDate(report.from)} – {formatDate(report.to)} ·{' '}
                {filters.moduleId
                  ? moduleTitle(filters.moduleId)
                  : 'Berky Digital insgesamt'}
              </p>
              <p>
                Vergleich: {formatDate(report.previousFrom)} –{' '}
                {formatDate(report.previousTo)}
              </p>
            </div>
            <MetricCardGrid>
              <MetricCard
                label="Nutzer im Zeitraum"
                value={formatNumber(report.activeUsers)}
                description={comparison(report.activeUsers, report.previousActiveUsers)}
              />
              <MetricCard
                label="Nutzungsquote"
                value={
                  report.knownUsers
                    ? `${formatNumber((report.activeKnownUsers / report.knownUsers) * 100)} %`
                    : '–'
                }
                description={`${formatNumber(report.activeKnownUsers)} von ${formatNumber(report.knownUsers)} bekannten, aktuell nicht gesperrten Nutzern`}
              />
              <MetricCard
                label="Einstiege"
                value={formatNumber(report.entries)}
                description={comparison(report.entries, report.previousEntries)}
              />
              <MetricCard
                label="Wiederkehrende Nutzer"
                value={formatNumber(report.returningUsers)}
                description="Im gewählten und im vorherigen Zeitraum aktiv"
              />
            </MetricCardGrid>
            <p className="text-sm text-muted-foreground">
              Heute:{' '}
              <strong className="text-foreground">
                {formatNumber(report.todayUsers)} Nutzer
              </strong>{' '}
              · Diese Kalenderwoche:{' '}
              <strong className="text-foreground">
                {formatNumber(report.weekUsers)} Nutzer
              </strong>{' '}
              · jeweils im gewählten Bereich, unabhängig vom Datumsfilter.
            </p>
            <Card>
              <CardHeader>
                <CardTitle>Nutzung im Zeitverlauf</CardTitle>
                <CardDescription>
                  Unterschiedliche Nutzer je Tag. Wählen Sie einen Tag, um die Auswertung
                  einzugrenzen.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {report.activeUsers === 0 ? (
                  <EmptyState
                    title="Keine Nutzung im Zeitraum"
                    description="Wählen Sie einen anderen Zeitraum oder Bereich."
                  />
                ) : (
                  <>
                    <div
                      className="flex gap-1 overflow-x-auto pb-2"
                      role="group"
                      aria-label="Nutzer pro Tag"
                    >
                      {days.map((day) => (
                        <Button
                          key={day.date}
                          variant="ghost"
                          className="h-44 min-w-8 flex-1 flex-col justify-end gap-2 rounded-md px-1 py-2"
                          title={`${formatDate(day.date)}: ${day.users} Nutzer, ${day.entries} Einstiege`}
                          aria-label={`${formatDate(day.date)}: ${day.users} Nutzer. Tag auswählen`}
                          onClick={() =>
                            onFilter({ ...filters, from: day.date, to: day.date })
                          }
                        >
                          <span
                            className="flex h-32 w-full flex-col justify-end gap-1"
                            aria-hidden="true"
                          >
                            <span className="text-xs">{day.users || ''}</span>
                            <span
                              className="block w-full rounded-t-sm bg-primary/80"
                              style={{ height: `${(day.users / maximum) * 80}%` }}
                            />
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {day.date.slice(8)}.
                          </span>
                        </Button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Kalendertage in Europe/Berlin. Bei langen Zeiträumen können Sie das
                      Diagramm seitlich verschieben.
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          </>
        )
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ChartColumn className="size-5" aria-hidden="true" />
            Welche Bereiche werden genutzt?
          </CardTitle>
          <CardDescription>
            Nach unterschiedlichen Nutzern sortiert. Klicken Sie auf einen Bereich für
            seine Unterseiten.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filters.moduleId && (
            <div className="mb-4 flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onFilter({ ...filters, moduleId: '' })}
              >
                Alle Bereiche anzeigen
              </Button>
            </div>
          )}
          <DataTable
            rows={ranking.data?.items ?? []}
            rowKey={(row) => row.moduleId}
            caption="Bereiche nach unterschiedlichen Nutzern"
            isLoading={ranking.isPending}
            error={
              ranking.isError
                ? {
                    description: 'Die Rangliste konnte nicht geladen werden.',
                    onRetry: () => void ranking.refetch(),
                  }
                : null
            }
            emptyState={
              <EmptyState
                title="Keine Bereiche genutzt"
                description="Für diesen Zeitraum wurden keine Einstiege erfasst."
              />
            }
            pagination={ranking.data}
            onPageChange={setRankPage}
            columns={[
              {
                key: 'module',
                header: 'Bereich',
                cell: (row) => (
                  <Button
                    variant="link"
                    className="h-auto p-0 text-left"
                    onClick={() => onFilter({ ...filters, moduleId: row.moduleId })}
                  >
                    {moduleTitle(row.moduleId)}
                    <ArrowRight aria-hidden="true" />
                  </Button>
                ),
              },
              {
                key: 'users',
                header: 'Unterschiedliche Nutzer',
                cell: (row) => (
                  <div className="min-w-32 space-y-1">
                    <span data-numeric>{formatNumber(row.users)}</span>
                    <div className="h-2 rounded-full bg-muted" aria-hidden="true">
                      <div
                        className="h-2 rounded-full bg-primary"
                        style={{ width: `${(row.users / maxUsers) * 100}%` }}
                      />
                    </div>
                  </div>
                ),
              },
              {
                key: 'entries',
                header: 'Einstiege',
                align: 'right',
                cell: (row) => formatNumber(row.entries),
              },
            ]}
          />
        </CardContent>
      </Card>

      {filters.moduleId && <PageRanking filters={filters} settings={settings} />}
      <p className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
        Als Nutzung zählt ein Einstieg in einen Bereich; Neuladen zählt erneut.
        Seitenwechsel innerhalb desselben Moduls erhöhen diesen Zähler nicht. Eine Person
        wird je Zeitraum nur einmal gezählt. Plattform-Einstiege über die Startseite
        werden erst seit dieser Erweiterung erfasst.
      </p>
    </div>
  )
}

function PageRanking({
  filters,
  settings,
}: {
  filters: UsageFilters
  settings: UsageSettings
}) {
  const api = usePlatformUsageApi()
  const [page, setPage] = useState(1)
  const pages = useQuery({
    queryKey: ['platform-usage', 'pages', filters, page],
    queryFn: ({ signal }) =>
      api.get<UsagePageResult<UsagePageRank>>('/pages', {
        query: { ...queryFilters(filters), seite: page },
        signal,
      }),
  })
  return (
    <Card>
      <CardHeader>
        <CardTitle>Unterseiten · {moduleTitle(filters.moduleId)}</CardTitle>
        <CardDescription>
          Seitenaufrufe aus der Testphase
          {settings.detailTrackingSince
            ? `, frühestens ab ${formatDate(settings.detailTrackingSince)}`
            : ', sobald erste Verläufe vorliegen'}
          . Zeiten sind Schätzwerte für aktive Nutzung.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          rows={pages.data?.items ?? []}
          rowKey={(row) => row.pagePath}
          caption="Meistbesuchte Unterseiten"
          isLoading={pages.isPending}
          pagination={pages.data}
          onPageChange={setPage}
          error={
            pages.isError
              ? {
                  description: 'Die Unterseiten konnten nicht geladen werden.',
                  onRetry: () => void pages.refetch(),
                }
              : null
          }
          emptyState={
            <EmptyState
              title="Noch keine Seitenverläufe"
              description="Frühere Tageszähler enthalten keine Unterseiten. Bei ausgeschalteter Testphase entstehen keine neuen Verläufe."
            />
          }
          columns={[
            {
              key: 'page',
              header: 'Unterseite',
              cell: (row) => pageTitle(row.pagePath, filters.moduleId),
            },
            {
              key: 'users',
              header: 'Nutzer',
              align: 'right',
              cell: (row) => formatNumber(row.users),
            },
            {
              key: 'visits',
              header: 'Aufrufe',
              align: 'right',
              cell: (row) => formatNumber(row.visits),
            },
            {
              key: 'duration',
              header: 'Aktive Zeit (ca.)',
              align: 'right',
              cell: (row) => duration(row.activeSeconds),
            },
          ]}
        />
      </CardContent>
    </Card>
  )
}
